// src/components/operationCenter/widgets/PortfolioAllocationWidget.tsx
// Portfolio allocation pie chart widget

import React, { useMemo, useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import { PieChart } from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";
import { usePortfolioHoldings } from "@/hooks/portfolio";
import { useAccessToken } from "@/hooks/useAccessToken";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { MetricKeys } from "@/constants/metrics";

const COLORS = [
  "#818cf8", // Indigo
  "#f472b6", // Pink
  "#34d399", // Emerald
  "#fbbf24", // Amber
  "#60a5fa", // Blue
  "#a78bfa", // Violet
  "#f87171", // Red
  "#4ade80", // Green
  "#fb923c", // Orange
  "#38bdf8", // Sky
];

const PortfolioAllocationWidget: React.FC<WidgetProps> = () => {
  const authToken = useAccessToken();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const [containerSize, setContainerSize] = useState({
    width: 300,
    height: 200,
  });

  const { data: holdingsData, isLoading: holdingsLoading } =
    usePortfolioHoldings(authToken);
  const { data: screenerRows } = useTokensScreenerData();

  // Measure container and resize chart
  useEffect(() => {
    const updateSize = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
        // Resize chart if it exists
        if (chartInstanceRef.current) {
          chartInstanceRef.current.resize();
        }
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Process allocation data
  const allocationData = useMemo(() => {
    const rawHoldings = holdingsData?.data ?? [];
    if (rawHoldings.length === 0 || !Array.isArray(screenerRows)) return [];

    const allocations = rawHoldings
      .map((h) => {
        const ticker = h.ticker.toUpperCase();
        const row = screenerRows.find(
          (r) => String(r["tokenSymbol"] ?? "").toUpperCase() === ticker
        );
        const currentPrice = row?.[MetricKeys.PriceClose] as number | null;
        const value = currentPrice
          ? h.quantity * currentPrice
          : h.quantity * h.avg_buy_price;

        return { ticker, value, name: h.token_name ?? ticker };
      })
      .filter((a) => a.value > 0)
      .sort((a, b) => b.value - a.value);

    // Group smaller holdings into "Other" if more than 8
    if (allocations.length <= 8) return allocations;

    const top7 = allocations.slice(0, 7);
    const otherValue = allocations
      .slice(7)
      .reduce((sum, a) => sum + a.value, 0);

    return [...top7, { ticker: "Other", value: otherValue, name: "Other" }];
  }, [holdingsData, screenerRows]);

  const totalValue = useMemo(
    () => allocationData.reduce((sum, a) => sum + a.value, 0),
    [allocationData]
  );

  // Render chart
  useEffect(() => {
    if (!chartRef.current || allocationData.length === 0) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const pieData = allocationData.map((a, i) => ({
      name: a.ticker,
      value: a.value,
      itemStyle: { color: COLORS[i % COLORS.length] ?? "#818cf8" },
    }));

    const option: EChartsOption = {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        textStyle: { color: "#f3f4f6", fontSize: 11 },
        formatter: (params: unknown) => {
          const p = params as { name: string; value: number; percent: number };
          const formatted =
            p.value >= 1e6
              ? `$${(p.value / 1e6).toFixed(2)}M`
              : p.value >= 1e3
              ? `$${(p.value / 1e3).toFixed(2)}K`
              : `$${p.value.toFixed(2)}`;
          return `<div style="font-weight:600;">${p.name}</div>
            <div style="margin-top:4px;">${formatted} (${p.percent.toFixed(
            1
          )}%)</div>`;
        },
      },
      legend: {
        orient: "vertical",
        right: 18,
        top: "middle",
        textStyle: { color: "#9ca3af", fontSize: 12 },
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 12,
        formatter: (name: string) => {
          const item = allocationData.find((a) => a.ticker === name);
          if (!item) return name;
          const pct =
            totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : "0";
          return `${name} ${pct}%`;
        },
      },
      series: [
        {
          type: "pie",
          radius: ["40%", "68%"],
          center: ["32%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 4,
            borderColor: "#1e293b",
            borderWidth: 2,
          },
          label: { show: false },
          emphasis: {
            label: { show: false },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          data: pieData,
        },
      ],
    };

    chart.setOption(option, true);

    return () => {
      // Cleanup handled in separate effect
    };
  }, [allocationData, totalValue, containerSize]);

  // Cleanup
  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  if (holdingsLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (allocationData.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <PieChart className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No allocation data</div>
        <div className="text-xs mt-1">Add holdings to see allocation</div>
      </div>
    );
  }

  const formatTotal = (val: number) => {
    if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(2)}K`;
    return `$${val.toFixed(2)}`;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <div className="flex items-center gap-2">
          <PieChart className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
            Portfolio Allocation
          </span>
        </div>
        <span className="text-xs font-semibold text-slate-200">
          {formatTotal(totalValue)}
        </span>
      </div>

      {/* Chart */}
      <div
        ref={chartRef}
        className="flex-1 min-h-0"
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default PortfolioAllocationWidget;
